export const CSV_LIST_SEPARATOR = "|";
export const CSV_DEFAULT_MARKER = "*";

export const EXPORT_VERSION = 2;

const GENDERS = ["MALE", "FEMALE"];

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : v);

const masterName = (ref) =>
  ref && typeof ref === "object" ? (ref.name ?? "") : "";

const toPortableRange = (range) => ({
  gender: range?.gender ?? "",
  normalRangeText: range?.normalRangeText ?? "",
  normalRangeMin: numOrNull(range?.normalRangeMin),
  normalRangeMax: numOrNull(range?.normalRangeMax),
});

const toPortableMethods = (methods) =>
  (Array.isArray(methods) ? methods : [])
    .map((entry) => ({
      name: masterName(entry?.method),
      isDefault: Boolean(entry?.isDefault),
    }))
    .filter((entry) => entry.name);

const toPortableParam = (p, idx) => ({
  name: p?.name ?? "",
  unit: p?.unit ?? "",
  normalRangeText: p?.normalRangeText ?? "",
  normalRangeMin: p?.normalRangeMin ?? null,
  normalRangeMax: p?.normalRangeMax ?? null,
  fieldType: p?.fieldType ?? "",
  required: Boolean(p?.required),
  displayOrder: p?.displayOrder ?? idx,
  referenceRanges: (Array.isArray(p?.referenceRanges) ? p.referenceRanges : [])
    .filter((range) => GENDERS.includes(range?.gender))
    .map(toPortableRange),
  methods: toPortableMethods(p?.methods),
  calculation:
    p?.calculation && masterName(p.calculation)
      ? { name: masterName(p.calculation) }
      : null,
  options: (Array.isArray(p?.options) ? p.options : [])
    .map((option) => (option ?? "").toString())
    .filter(Boolean),
});

export const toPortableTemplate = (t) => ({
  name: t?.name ?? "",
  code: t?.code ?? "",
  reportKind: t?.reportKind ?? "",
  investigationType: t?.investigationType
    ? {
      name: t.investigationType.name ?? "",
      code: t.investigationType.code ?? "",
    }
    : null,
  test: t?.test ? { name: t.test.name ?? "" } : null,
  isActive: t?.isActive !== false,
  isDefault: Boolean(t?.isDefault),
  parameters: Array.isArray(t?.parameters)
    ? t.parameters.map(toPortableParam)
    : [],
});

export const buildExportJson = (templates, scope) =>
  JSON.stringify(
    {
      version: EXPORT_VERSION,
      exportedAt: new Date().toISOString(),
      scope,
      templates: (templates || []).map(toPortableTemplate),
    },
    null,
    2,
  );

export const CSV_HEADERS_V1 = [
  "templateName",
  "templateCode",
  "investigationTypeCode",
  "investigationTypeName",
  "testName",
  "reportKind",
  "isActive",
  "isDefault",
  "parameterName",
  "parameterUnit",
  "parameterReferenceRange",
  "parameterMin",
  "parameterMax",
  "parameterInputType",
  "parameterRequired",
  "parameterOrder",
];

export const CSV_HEADERS_V2 = [
  "parameterMaleRange",
  "parameterMaleMin",
  "parameterMaleMax",
  "parameterFemaleRange",
  "parameterFemaleMin",
  "parameterFemaleMax",
  "parameterMethods",
  "parameterCalculation",
  "parameterOptions",
];

export const CSV_HEADERS = [...CSV_HEADERS_V1, ...CSV_HEADERS_V2];

const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const rangeFor = (param, gender) =>
  (param.referenceRanges || []).find((r) => r.gender === gender) || {};

export const encodeMethodsCell = (methods) =>
  (methods || [])
    .map((m) => `${m.name}${m.isDefault ? CSV_DEFAULT_MARKER : ""}`)
    .join(CSV_LIST_SEPARATOR);

export const encodeOptionsCell = (options) =>
  (options || []).join(CSV_LIST_SEPARATOR);

const parameterCells = (p) => {
  const male = rangeFor(p, "MALE");
  const female = rangeFor(p, "FEMALE");
  return [
    p.name,
    p.unit,
    p.normalRangeText,
    p.normalRangeMin ?? "",
    p.normalRangeMax ?? "",
    p.fieldType,
    p.required,
    p.displayOrder,
    male.normalRangeText ?? "",
    male.normalRangeMin ?? "",
    male.normalRangeMax ?? "",
    female.normalRangeText ?? "",
    female.normalRangeMin ?? "",
    female.normalRangeMax ?? "",
    encodeMethodsCell(p.methods),
    p.calculation?.name || "",
    encodeOptionsCell(p.options),
  ];
};

export const buildExportCsv = (templates) => {
  const lines = [CSV_HEADERS.join(",")];
  (templates || []).map(toPortableTemplate).forEach((t) => {
    const base = [
      t.name,
      t.code,
      t.investigationType?.code || "",
      t.investigationType?.name || "",
      t.test?.name || "",
      t.reportKind,
      t.isActive,
      t.isDefault,
    ];
    if (!t.parameters.length) {
      const blanks = new Array(CSV_HEADERS.length - base.length).fill("");
      lines.push([...base, ...blanks].map(csvCell).join(","));
      return;
    }
    t.parameters.forEach((p) => {
      lines.push([...base, ...parameterCells(p)].map(csvCell).join(","));
    });
  });
  return lines.join("\r\n");
};

export const exportFilename = (scope, ext) => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `report-templates-${scope || "all"}-${stamp}.${ext}`;
};

export const downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const collectAllPages = async (
  fetchPage,
  { perPage = 200, maxPages = 1000 } = {},
) => {
  const all = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const res = (await fetchPage(page, perPage)) || {};
    const items = Array.isArray(res.items) ? res.items : [];
    all.push(...items);
    const totalPages = Number(res.totalPages) || 0;
    const total = Number(res.total) || 0;
    const pagesDone = totalPages > 0 && page >= totalPages;
    const totalDone = total > 0 && all.length >= total;
    const shortPage = items.length < perPage;
    if (pagesDone || totalDone || shortPage || items.length === 0) break;
  }
  return all;
};

// format: "json" | "csv"
export const exportReportTemplates = (templates, format, scope) => {
  if (format === "csv") {
    downloadFile(
      buildExportCsv(templates),
      exportFilename(scope, "csv"),
      "text/csv;charset=utf-8;",
    );
  } else {
    downloadFile(
      buildExportJson(templates, scope),
      exportFilename(scope, "json"),
      "application/json;charset=utf-8;",
    );
  }
};
